import { useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  descartarAssinaturaPendente, fetchSubscriptionStatus, type BillingCycle, type SubscriptionState,
} from '../../store/slices/subscription';

import { fmtBRL } from '../../hooks/usePlanPrices';
import type { Avisar, Ir } from '../porta';

// A ASSINATURA PENDENTE (gerada e ainda não paga), com saída (pedido do Lucas, 04/10/2026).
//
// Antes, quem gerava um PIX ficava preso nele: "Retomar" trazia sempre o mesmo QR, "Ver
// assinatura" ia para as Configurações (que no Pix Automático diziam "você não tem assinatura"
// e mandavam de volta), e não havia como trocar para o cartão quando o PIX do banco dava erro,
// nem corrigir a quantidade de perfis escolhida por engano.
//
// Agora, onde a assinatura pendente aparece (Planos, a tela do QR, as Configurações), aparece O QUE
// foi escolhido e as duas saídas: pagar o que já foi gerado, ou "Alterar plano ou pagamento", que
// descarta no servidor (depois de conferir na Asaas que nada caiu) e volta ao pagamento com as
// escolhas de antes já marcadas. Sem a palavra "pedido" (decisão do Lucas).

const FORMA: Record<string, string> = {
  PIX: 'PIX',
  PIX_AUTOMATIC: 'Pix Automático',
  CREDIT_CARD: 'Cartão de crédito',
};

type DadosDaPendente = Pick<SubscriptionState, 'cycle' | 'value' | 'billingType' | 'includedProfiles' | 'extraProfiles' | 'extraStorageGb'>;

/** O que foi escolhido, para a pessoa conferir antes de pagar (e ver o que quer mudar). */
export const resumoDaPendente = (s: DadosDaPendente): { rotulo: string; valor: string }[] => {
  const anual = s.cycle === 'YEARLY';
  const perfis = Math.max(1, (s.includedProfiles || 1) + (s.extraProfiles || 0));
  return [
    { rotulo: 'Plano', valor: `Maestra PRO ${anual ? 'anual' : 'mensal'}` },
    { rotulo: 'Perfis de artista', valor: `${perfis} ${perfis === 1 ? 'perfil' : 'perfis'}` },
    ...(s.extraStorageGb > 0 ? [{ rotulo: 'Espaço a mais', valor: `${s.extraStorageGb} GB` }] : []),
    ...(s.value !== null && s.value !== undefined ? [{ rotulo: anual ? 'Valor por ano' : 'Valor por mês', valor: fmtBRL(Number(s.value)) }] : []),
    { rotulo: 'Forma de pagamento', valor: (s.billingType && FORMA[s.billingType]) || 'PIX' },
  ];
};

/** No cartão não há QR: o que se paga é a análise da operadora. */
export const textosDaPendente = (billingType: string | null) => (billingType === 'CREDIT_CARD'
  ? {
    titulo: 'O pagamento da sua assinatura está em análise',
    texto: 'A operadora do cartão está processando o pagamento. Se quiser, altere o plano ou pague de outro jeito.',
    pagar: 'Ver o pagamento',
    confirmar: 'O pagamento no cartão, ainda em análise, é cancelado, e você escolhe de novo os perfis e a forma de pagamento. Nada foi cobrado.',
  }
  : {
    titulo: 'Sua assinatura está esperando o pagamento',
    texto: 'O PIX já foi gerado. Pague o mesmo código, ou altere o plano e a forma de pagamento.',
    pagar: 'Pagar com o PIX gerado',
    confirmar: 'O PIX gerado deixa de valer, e você escolhe de novo os perfis e a forma de pagamento (PIX ou cartão). Nada foi cobrado.',
  });

/** As escolhas de antes, que voltam marcadas no pagamento. */
export interface EscolhaAnterior {
  ciclo: BillingCycle;
  perfisExtras: number;
  gbExtras: number;
}

/**
 * Alterar ou cancelar a assinatura pendente. `alterar` volta ao pagamento com as escolhas de antes;
 * `cancelar` só descarta (a conta volta ao plano grátis). Se o pagamento já caiu, nada se descarta:
 * a tela vai para a confirmação.
 */
export const useAlterarPendente = ({ ir: navigate, avisar: message }: { ir: Ir; avisar: Avisar }) => {
  const dispatch = useAppDispatch();
  const { cycle, extraProfiles, extraStorageGb, billingType } = useAppSelector((s) => s.subscription);
  const [aberto, setAberto] = useState(false);
  const [emCurso, setEmCurso] = useState(false);

  const descartar = async (depois: 'escolher' | 'ficar') => {
    // As escolhas saem do estado ANTES do descarte (ele as zera).
    const escolhaAnterior: EscolhaAnterior = {
      ciclo: cycle === 'YEARLY' ? 'YEARLY' : 'MONTHLY',
      perfisExtras: extraProfiles || 0,
      gbExtras: extraStorageGb || 0,
    };
    setEmCurso(true);
    try {
      const r = await dispatch(descartarAssinaturaPendente()).unwrap();
      if (r?.jaPago) {
        message.info('O pagamento já foi feito. Confirmando a sua assinatura.');
        navigate('/pagamento', { replace: true });
        return;
      }
      // O cartão na análise da operadora não se descarta: ela pode aprovar depois, e a cobrança
      // ficaria sem assinatura (ou em dobro). A espera do cartão atualiza sozinha.
      if (r?.emAnalise) {
        message.info('O cartão ainda está em análise pela operadora. Espere a resposta, que costuma levar poucos minutos.');
        navigate('/pagamento', { replace: true });
        return;
      }
      void dispatch(fetchSubscriptionStatus());
      if (r?.naoPendente) return;
      if (depois === 'escolher') navigate('/planos', { replace: true, state: { escolhaAnterior } });
      else message.success('Assinatura pendente cancelada. Nada foi cobrado.');
    } catch (e) {
      message.error(typeof e === 'string' ? e : 'Não foi possível alterar a assinatura agora. Tente de novo em instantes.');
    } finally {
      setEmCurso(false);
      setAberto(false);
    }
  };

  return {
    aberto,
    emCurso,
    texto: textosDaPendente(billingType).confirmar,
    pedir: () => setAberto(true),
    fechar: () => { if (!emCurso) setAberto(false); },
    /** Descarta e volta ao pagamento, com as escolhas de antes marcadas. */
    alterar: () => { void descartar('escolher'); },
    /** Só descarta: a conta fica no plano grátis. */
    cancelar: () => { void descartar('ficar'); },
  };
};

export type AlterarPendente = ReturnType<typeof useAlterarPendente>;
